import * as React from "react";
import { Layout, type LayoutProps } from "./Layout";
import { useTileSize } from "./context";
import type { TileSizes } from "./types";
import { useMediaQuery } from "@react-hookz/web";

export interface TileProps extends Omit<LayoutProps, "size" | "withLabel"> {
  size?: TileSizes;
  children?: never;
  /**
   * When the label overflows it should show a tooltip on the on the `Tile` component.
   * @default true
   */
  shouldShowTooltip?: boolean;
  /**
   * If you need to control the element that is used as the Tile's label, you can use this prop.
   * It supports an `as` prop to change the element type to any other element if the need arises.
   */
  withLabelProps?: Omit<
    React.ComponentProps<typeof Layout.ActionLabel>,
    "children"
  >;
  withLabel: string;
}

export const _Tile: React.FC<TileProps> = ({
  className,
  size: _size,
  withLabel,
  withLabelProps,
  withSubtext,
  withStartAccessory: withStartAccessory,
  withEndAccessory: withEndAccessory,
  isFullWidth,
  endAccessoryLayout: _endAccessoryLayout = "floating",
  shouldAlwaysShowEndAccessory: shouldAlwaysShowEndAccessory,
  shouldShowTooltip = true,
  ...rest
}) => {
  const layoutRef = React.useRef<HTMLSpanElement>(null);
  const isFineInput = useMediaQuery("(pointer: fine)");
  const isCoarseInput = useMediaQuery("(pointer: coarse)");
  const [hasOverflowLabel, setHasOverflowLabel] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const pointerType =
    !isFineInput && !isCoarseInput ? "fine" : isFineInput ? "fine" : "coarse";

  // automatically assume an end accessory layout type if one is not provided
  const endAccessoryLayout: TileProps["endAccessoryLayout"] =
    _endAccessoryLayout
      ? _endAccessoryLayout
      : pointerType === "fine"
        ? "floating"
        : "inline";
  const size = useTileSize({ size: _size });

  const shouldShowEndAccessory = withEndAccessory && size !== "small";

  const shouldShowSubtext = withSubtext && size !== "small";

  const handleTruncateChange = React.useCallback(
    (isTruncated) => {
      if (withLabelProps?.onTruncateChange) {
        withLabelProps.onTruncateChange(isTruncated);
      }
      setHasOverflowLabel(isTruncated);
    },
    [withLabelProps?.onTruncateChange],
  );

  const showTooltip = shouldShowTooltip ? isHovered && hasOverflowLabel : false;

  return (
    <Layout
      ref={layoutRef}
      className={className}
      size={size}
      withLabel={
        <Layout.ActionLabel
          {...withLabelProps}
          onTruncateChange={handleTruncateChange}
          showTooltip={showTooltip}
        >
          {withLabel}
        </Layout.ActionLabel>
      }
      withSubtext={
        shouldShowSubtext && <Layout.SubText>{withSubtext}</Layout.SubText>
      }
      withStartAccessory={withStartAccessory}
      withEndAccessory={shouldShowEndAccessory && withEndAccessory}
      isFullWidth={isFullWidth}
      endAccessoryLayout={endAccessoryLayout}
      shouldAlwaysShowEndAccessory={shouldAlwaysShowEndAccessory}
      onPointerEnter={(e) => {
        if (rest.onPointerEnter) rest.onPointerEnter(e);
        setIsHovered(true);
      }}
      onPointerLeave={(e) => {
        if (rest.onPointerLeave) rest.onPointerLeave(e);
        setIsHovered(false);
      }}
      onFocus={(e) => {
        if (rest.onFocus) rest.onFocus(e);
        setIsHovered(true);
      }}
      onBlur={(e) => {
        if (rest.onBlur) rest.onBlur(e);

        // if the related target is inside of the tile layout do not set isHovered to false
        // because we just end up blinking a state that should not have changed to begin with.
        if (
          e.relatedTarget &&
          layoutRef.current?.contains(e.relatedTarget as Node)
        ) {
          return;
        }

        setIsHovered(false);
      }}
      {...rest}
    />
  );
};
